import React, { FC, useState, useRef, useMemo } from 'react'
import ModalBox from 'components/Common/ModalBox'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { CalendarClock } from 'lucide-react'
import colors from 'colors'
import { BlockDetailModal } from './components/Modals/BlockDetailModal'
import { CancelConfirmationModal } from './components/Modals/CancelConfirmationModal'
import { TimezoneSelector } from './components/TimezoneSelector/TimezoneSelector'
import { SuggestedTimes } from './components/SuggestedTimes/SuggestedTimes'
import { GuestManagement } from './components/GuestManagement/GuestManagement'
import { MeetingDetailsForm } from './components/MeetingDetailsForm/MeetingDetailsForm'
import { DateTimePicker } from './components/DateTimePicker/DateTimePicker'
import FindTimeCalendar from './components/FindTimeCalendar/FindTimeCalendar'
import {
  useAttendeeCalendar,
  useCalendarData,
  useMeetingHandlers,
  useSuggestedTimes,
  useAssigneeOptions,
  useModalEffects,
  useTimeComputations,
} from './hooks'
import type { ICreateMeetingPopupProps } from './types'
import {
  formatTimeString,
  addMinutesToTime,
  getTimezoneDisplay,
  getValidStartTimes,
  getValidEndTimes,
} from './helpers'

const CreateMeetingPopup: FC<ICreateMeetingPopupProps> = ({ isOpen, onClose }) => {
  // Memoize device timezone to avoid repeated calls
  const deviceTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  const [meetingTitle, setMeetingTitle] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [showCalendar, setShowCalendar] = useState(false)
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date())
  const [startTime, setStartTime] = useState<string>('')
  const [endTime, setEndTime] = useState<string>('')
  const [showStartTimePicker, setShowStartTimePicker] = useState(false)
  const [showEndTimePicker, setShowEndTimePicker] = useState(false)
  const [showFindTimeCalendar, setShowFindTimeCalendar] = useState(false)
  const [findTimeView, setFindTimeView] = useState<'Day' | '3 Days' | 'Week'>('3 Days')
  const [findTimeCurrentDate, setFindTimeCurrentDate] = useState(new Date())
  const [guestSearchQuery, setGuestSearchQuery] = useState('')
  const [showGuestDropdown, setShowGuestDropdown] = useState(false)
  const [selectedGuests, setSelectedGuests] = useState<IAssigneeOptions[]>([])
  const [emailError, setEmailError] = useState('')
  const [selectedTimezone, setSelectedTimezone] = useState<string>(deviceTimezone)
  const [highlightedGuestIndex, setHighlightedGuestIndex] = useState(-1)
  const [selectedAttendeeFilter, setSelectedAttendeeFilter] = useState<string[]>(['all']) // array of 'all' or userIds for multi-select
  const [meetingLink, setMeetingLink] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [meetingDescription, setMeetingDescription] = useState('')
  const [meetingLocation, setMeetingLocation] = useState('')
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [selectedBlock, setSelectedBlock] = useState<{
    attendee: IAssigneeOptions
    title?: string
    color: string
    startHour: number
    endHour: number
    startMinute: number
    endMinute: number
    date: Date
  } | null>(null)
  const [temporarySlot, setTemporarySlot] = useState<{
    date: Date
    startHour: number
    startMinute: number
  } | null>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const startTimeRef = useRef<HTMLDivElement>(null)
  const endTimeRef = useRef<HTMLDivElement>(null)
  const timeGridRef = useRef<HTMLDivElement>(null)
  const guestItemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const isCalendarClickRef = useRef(false) // Track if time change is from calendar click

  // Get current user info
  const currentUser = useSelector((state: RootState) => state.user.user)
  const tenantId = useSelector((state: RootState) => state.user.tenantId)
  const isAdvisor =
    currentUser?.roles?.includes('BSO_ADVISOR') || currentUser?.roles?.includes('BSO_ADMIN')

  // Get assignee options based on user role
  const assigneeOptions = useAssigneeOptions({ isAdvisor, tenantId })

  // Use custom hook for calendar data fetching (computes attendees internally)
  const { userAvailabilityMap, pendingGuestRequests, fetchedGuestsRef } = useCalendarData({
    showFindTimeCalendar,
    selectedGuests,
    findTimeCurrentDate,
  })

  // Use custom hook for attendee calendar logic with fetched data
  const { allAttendees, internalAttendees, attendeeColors, getAttendeeEvents } =
    useAttendeeCalendar({
      selectedGuests,
      userAvailabilityMap,
    })

  // Use custom hook for suggested times calculation
  const { suggestedTimes, loadingSuggestions } = useSuggestedTimes({
    selectedGuests,
    internalAttendees,
    userAvailabilityMap,
    getAttendeeEvents,
  })

  // Filter users based on search query and exclude already selected guests and the organizer
  const filteredUsers = useMemo(() => {
    if (!assigneeOptions) {
      return []
    }

    return assigneeOptions.filter((user) => {
      const matchesSearch = user.label.toLowerCase().includes(guestSearchQuery.toLowerCase())
      const notAlreadySelected = !selectedGuests.some((guest) => guest.value === user.value)
      const notOrganizer = user.value !== currentUser.id

      return matchesSearch && notAlreadySelected && notOrganizer
    })
  }, [assigneeOptions, guestSearchQuery, selectedGuests, currentUser.id])

  // Use custom hook for all event handlers
  const {
    handleDateSelect,
    handleStartTimeSelect,
    handleEndTimeSelect,
    handleGuestSelect,
    handleRemoveGuest,
    handleAddGuestByEmail,
    handleCopyMeetingLink,
    handleGuestInputKeyPress,
    handleSuggestedTimeClick,
    handleCancelClick,
    handleConfirmCancel,
    handleCancelConfirmation,
    handleCreateMeeting,
  } = useMeetingHandlers({
    selectedGuests,
    setSelectedGuests,
    guestSearchQuery,
    setGuestSearchQuery,
    setShowGuestDropdown,
    setEmailError,
    setHighlightedGuestIndex,
    fetchedGuestsRef,
    pendingGuestRequests,
    setSelectedDate,
    setShowCalendar,
    setShowStartTimePicker,
    setShowEndTimePicker,
    setStartTime,
    setEndTime,
    endTime,
    setTemporarySlot,
    isCalendarClickRef,
    meetingLink,
    setLinkCopied,
    filteredUsers,
    highlightedGuestIndex,
    meetingTitle,
    selectedDate,
    startTime,
    selectedTimezone,
    currentUser,
    tenantId,
    isAdvisor,
    meetingDescription,
    meetingLocation,
    onClose,
    setShowCancelConfirmation,
    setMeetingTitle,
    setMeetingLink,
    setMeetingDescription,
    setMeetingLocation,
  })

  // Use custom hook for modal side effects (auto-scroll, reset on close, etc.)
  useModalEffects({
    isOpen,
    showFindTimeCalendar,
    selectedTimezone,
    highlightedGuestIndex,
    startTime,
    deviceTimezone,
    timeGridRef,
    guestItemRefs,
    temporarySlot,
    isCalendarClickRef,
    selectedDate,
    fetchedGuestsRef,
    pendingGuestRequests,
    setShowCalendar,
    setShowStartTimePicker,
    setShowEndTimePicker,
    setShowFindTimeCalendar,
    setShowGuestDropdown,
    setGuestSearchQuery,
    setSelectedGuests,
    setEmailError,
    setSelectedDate,
    setStartTime,
    setEndTime,
    setSelectedTimezone,
    setCurrentCalendarDate,
    setFindTimeCurrentDate,
    setFindTimeView,
    setMeetingTitle,
    setMeetingLink,
    setMeetingDescription,
    setMeetingLocation,
    setLinkCopied,
    setTemporarySlot,
    setSelectedAttendeeFilter,
  })

  // Use custom hook for time-related computations
  const { displayedStartTime, displayedEndTime } = useTimeComputations({
    selectedDate,
    startTime,
    endTime,
    selectedTimezone,
    deviceTimezone,
  })

  if (!isOpen) {
    return null
  }

  return (
    <>
      <ModalBox
        title="Create Meeting"
        onClose={onClose}
        height="h-auto max-h-[90vh]"
        width="max-w-2xl"
        padding="xs:p-6 sm:p-4"
        bgColor="bg-black/50 backdrop-blur-sm"
        childClass="overflow-hidden rounded-b-xl"
      >
        <div className="px-6 pb-6 pt-2 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Subtitle */}
          <p className="text-sm text-silver-light mb-6">
            Schedule a meeting with anyone in GoTackle
          </p>

          {/* Meeting Title */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-primary-text mb-2">
              Meeting Title
            </label>
            <input
              type="text"
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
              placeholder="e.g., Q4 Strategy Review"
              className="w-full rounded-md border border-primary-outline px-3 py-2 text-sm focus:border-primary focus:ring-primary focus:outline-none"
            />
          </div>

          {/* Date & Time */}
          <DateTimePicker
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            showCalendar={showCalendar}
            onShowCalendarChange={setShowCalendar}
            currentCalendarDate={currentCalendarDate}
            onCurrentCalendarDateChange={setCurrentCalendarDate}
            startTime={startTime}
            onStartTimeSelect={handleStartTimeSelect}
            showStartTimePicker={showStartTimePicker}
            onShowStartTimePickerChange={setShowStartTimePicker}
            displayedStartTime={displayedStartTime}
            validStartTimes={getValidStartTimes(selectedDate, selectedTimezone, deviceTimezone)}
            endTime={endTime}
            onEndTimeSelect={handleEndTimeSelect}
            showEndTimePicker={showEndTimePicker}
            onShowEndTimePickerChange={setShowEndTimePicker}
            displayedEndTime={displayedEndTime}
            validEndTimes={getValidEndTimes(startTime)}
            calendarRef={calendarRef}
            startTimeRef={startTimeRef}
            endTimeRef={endTimeRef}
          />

          {/* Timezone */}
          <div className="flex items-center justify-between mb-6 gap-4">
            <TimezoneSelector
              selectedTimezone={selectedTimezone}
              onTimezoneChange={setSelectedTimezone}
              deviceTimezone={getTimezoneDisplay(selectedTimezone)}
            />

            <button
              className="text-sm border border-primary-outline text-primary hover:bg-primary-brandLight flex items-center gap-1 px-3 py-1.5 rounded-md whitespace-nowrap flex-shrink-0"
              onClick={() => {
                setShowFindTimeCalendar(!showFindTimeCalendar)
              }}
            >
              <CalendarClock className="w-4 h-4" color={colors.primary.DEFAULT} />
              {showFindTimeCalendar ? 'Suggested Times' : 'Find Time'}
            </button>
          </div>

          {/* Find Time Calendar - Shows when Find Time is clicked */}
          <FindTimeCalendar
            show={showFindTimeCalendar}
            view={findTimeView}
            currentDate={findTimeCurrentDate}
            selectedAttendeeFilter={selectedAttendeeFilter}
            allAttendees={allAttendees}
            getAttendeeEvents={getAttendeeEvents}
            attendeeColors={attendeeColors}
            temporarySlot={temporarySlot}
            timezone={selectedTimezone}
            timeGridRef={timeGridRef}
            isCalendarClickRef={isCalendarClickRef}
            onViewChange={setFindTimeView}
            onDateChange={setFindTimeCurrentDate}
            onMonthChange={(monthIndex) => {
              const newDate = new Date(findTimeCurrentDate)
              newDate.setMonth(monthIndex)
              setFindTimeCurrentDate(newDate)
            }}
            onAttendeeFilterChange={setSelectedAttendeeFilter}
            onTimeSlotClick={(date, hour, startMinute) => {
              // Set flag to indicate this is a calendar click (prevents clearing temporary slot)
              isCalendarClickRef.current = true

              // Set the selected date
              setSelectedDate(new Date(date))

              // Set temporary slot for visual feedback FIRST
              setTemporarySlot({
                date: new Date(date),
                startHour: hour,
                startMinute: startMinute,
              })

              // Format and set start time
              const startTimeString = formatTimeString(hour, startMinute)
              setStartTime(startTimeString)

              // Calculate end time (15 minutes later)
              const endTimeString = addMinutesToTime(startTimeString, 15)
              setEndTime(endTimeString)
            }}
            onBlockClick={setSelectedBlock}
          />

          {/* Suggested Times - Show when guests are added and Find Time calendar is not showing */}
          {(loadingSuggestions || suggestedTimes.length > 0) &&
            !showFindTimeCalendar &&
            selectedGuests.length > 0 && (
              <SuggestedTimes
                suggestedTimes={suggestedTimes}
                isLoading={loadingSuggestions}
                onTimeSelect={handleSuggestedTimeClick}
              />
            )}

          {/* Guest Management */}
          <GuestManagement
            guestSearchQuery={guestSearchQuery}
            onSearchQueryChange={setGuestSearchQuery}
            showDropdown={showGuestDropdown}
            onShowDropdownChange={setShowGuestDropdown}
            emailError={emailError}
            onEmailErrorChange={setEmailError}
            filteredUsers={filteredUsers}
            highlightedIndex={highlightedGuestIndex}
            onHighlightedIndexChange={setHighlightedGuestIndex}
            selectedGuests={selectedGuests}
            onGuestSelect={handleGuestSelect}
            onGuestRemove={handleRemoveGuest}
            onAddGuestByEmail={handleAddGuestByEmail}
            onKeyPress={handleGuestInputKeyPress}
            guestItemRefs={guestItemRefs}
            currentUserId={currentUser.id}
            allAttendees={allAttendees}
          />

          {/* Meeting Details Form */}
          <MeetingDetailsForm
            location={meetingLocation}
            onLocationChange={setMeetingLocation}
            meetingLink={meetingLink}
            onMeetingLinkChange={setMeetingLink}
            linkCopied={linkCopied}
            onCopyLink={handleCopyMeetingLink}
            description={meetingDescription}
            onDescriptionChange={setMeetingDescription}
          />

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-3 border-t border-primary-outline">
            <button
              onClick={handleCancelClick}
              className="px-4 py-2 border border-primary-outline text-primary-text hover:text-primary text-sm rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateMeeting}
              className="px-5 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary"
            >
              Create Meeting
            </button>
          </div>
        </div>
      </ModalBox>

      {/* Block Detail Zoom Modal */}
      <BlockDetailModal block={selectedBlock} onClose={() => setSelectedBlock(null)} />

      {/* Cancel Confirmation Dialog */}
      <CancelConfirmationModal
        isOpen={showCancelConfirmation}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelConfirmation}
      />
    </>
  )
}

export default CreateMeetingPopup
